/**
 * Money is decimal; doubles are not.
 *
 * A plan that spends the paycheck to the penny does not subtract to zero in
 * binary floating point — it lands a hair below, which renders as -$0.00 and
 * styles a balanced paycheck as short. Settle every answer in whole cents.
 */

export function roundCents(value: number): number {
  const cents = Math.round(value * 100);
  // -0 formats as "-$0.00", so normalize it away.
  return cents === 0 ? 0 : cents / 100;
}

/** Cent-accurate sum. Adding rounded values is not the same as rounding a sum. */
export function sumCents(values: readonly number[]): number {
  return roundCents(values.reduce((total, value) => total + value, 0));
}

export function centsEqual(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100);
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatMoney(value: number): string {
  return usd.format(roundCents(value));
}

/** For dashboard figures where cents are noise. Never for a ledger row. */
export function formatMoneyWhole(value: number): string {
  return usdWhole.format(roundCents(value));
}

/**
 * Money out reads as `-$12.34`, money in as `+$12.34`. The sign is explicit
 * because color alone must never carry the meaning.
 */
export function formatSignedMoney(value: number): string {
  const rounded = roundCents(value);
  if (rounded === 0) return usd.format(0);
  const sign = rounded > 0 ? '+' : '-';
  return `${sign}${usd.format(Math.abs(rounded))}`;
}

/** `formatMoney` without the currency symbol, for inputs. */
export function formatAmountInput(value: number): string {
  return roundCents(value).toFixed(2);
}

/**
 * Parses what someone typed into a money field, tolerating `$`, commas, and
 * spaces. Returns `null` for anything that is not a number so callers can show
 * a real error instead of silently treating it as zero.
 */
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!cleaned || !/^-?\d*\.?\d*$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? roundCents(value) : null;
}
